import { QueryResponseType } from '@1po/1po-bff-fe-spec/generated/common/WsResponse';
import { GetMyStoreBundlesResponse } from '@1po/1po-bff-fe-spec/generated/my_store/bundles/response/GetMyStoreBundlesResponse';
import { GetMyStoreReferencesResponse } from '@1po/1po-bff-fe-spec/generated/my_store/references/response/GetMyStoreReferencesResponse';
import { UpdateMyStoreTireDiscount } from '@1po/1po-bff-fe-spec/generated/my_store/tires/request/UpdateMyStoreTireDiscountList';
import { GetMyStoreTiresResponse } from '@1po/1po-bff-fe-spec/generated/my_store/tires/response/GetMyStoreTiresResponse';
import { put, takeEvery } from '@redux-saga/core/effects';
import { SagaIterator } from 'redux-saga';
import { v4 } from 'uuid';
import { RootState } from 'app/AppStore';
import {
  sendAddBundleRequest,
  sendAddBundlesSectionRequest,
  sendAddReferencesSectionRequest,
  sendCreateReferenceRequest,
  sendDeleteBundlesSectionRequest,
  sendDeleteReferencesSectionRequest,
  sendGetBundlesRequest,
  sendGetReferencesRequest,
  sendMoveBundleSectionRequest,
  sendMoveReferenceSectionRequest,
  sendRemoveBundleRequest,
  sendRemoveReferenceRequest,
  sendRenameBundlesSectionRequest,
  sendRenameReferencesSectionRequest,
  sendSubscribeTiresRequest,
  sendUpdateBundleCode,
  sendUpdateBundleDesignation,
  sendUpdateBundlePrice,
  sendUpdateMyStoreTireDiscountListRequest,
  sendUpdateReferenceRequest,
} from 'domains/myStore/MyStore.api';
import * as actions from 'domains/myStore/MyStore.store';
import {
  addNewReference,
  clearMyStoreTiresChanges,
  getBundlesSections,
  getMyStoreBundlesId,
  getMyStoreReferencesId,
  getMyStoreTiresId,
  getReferencesSections,
  getSectionReference,
  removeReference,
  reorderBundles,
  reorderReferences,
  setBundlesSection,
  setBundlesStatus,
  setInitialState,
  setMyStoreTireBrands,
  setMyStoreTiresId,
  setMyStoreTiresSearchStatus,
  setReferencesSection,
  setReferencesStatus,
  updateReferenceInSection,
} from 'domains/myStore/MyStore.store';
import { WsResponse } from 'domains/webSockets/WebSocket.types';
import { notifyTop } from 'UI/Notification/notification';
import { AppTranslation, FOUND, LOADING, sagaGuard, select } from 'utils';

export function* initMyStoreSaga(): SagaIterator {
  yield put(setInitialState());
}

export function* getBundlesRequestSaga(): SagaIterator {
  yield put(setBundlesStatus(LOADING));
  yield put(sendGetBundlesRequest());
}

export function* getBundlesResponseSaga(action: WsResponse<GetMyStoreBundlesResponse>): SagaIterator {
  yield put(setBundlesSection(action.payload));
  yield put(setBundlesStatus(FOUND));
}

export function* moveBundlesSectionRequestSaga({
  payload,
}: ReturnType<typeof actions.moveBundlesSectionRequestSaga>): SagaIterator {
  const { active, over } = payload;
  const myStoreBundlesId = yield* select(getMyStoreBundlesId);
  const bundles = yield* select(getBundlesSections);
  const index = bundles.sections.data?.findIndex((bundle) => bundle.id === over?.id) ?? -1;
  if (index > -1 && myStoreBundlesId) {
    yield put(reorderBundles(payload));
    yield put(sendMoveBundleSectionRequest({ myStoreBundlesId, sectionId: active.id.toString(), index }));
  }
}

export function* addBundlesSectionRequestSaga(): SagaIterator {
  const myStoreBundlesId = yield* select(getMyStoreBundlesId);
  const sectionId = v4();
  if (myStoreBundlesId) {
    yield put(sendAddBundlesSectionRequest({ myStoreBundlesId, sectionId }));
  }
}

export function* renameBundlesSectionRequestSaga({
  payload,
}: ReturnType<typeof actions.renameBundlesSectionRequestSaga>): SagaIterator {
  const { sectionId, title } = payload;
  const myStoreBundlesId = yield* select(getMyStoreBundlesId);
  if (myStoreBundlesId) {
    yield put(sendRenameBundlesSectionRequest({ myStoreBundlesId, sectionId, title }));
  }
}

export function* addBundleRequestSaga({ payload }: ReturnType<typeof actions.addBundleRequestSaga>): SagaIterator {
  const { sectionId } = payload;
  const myStoreBundlesId = yield* select(getMyStoreBundlesId);
  const bundleId = v4();
  if (myStoreBundlesId) {
    yield put(sendAddBundleRequest({ myStoreBundlesId, sectionId, bundleId }));
  }
}

export function* removeBundleSectionReuestSaga({
  payload,
}: ReturnType<typeof actions.removeBundleSectionReuestSaga>): SagaIterator {
  const { sectionId } = payload;
  const myStoreBundlesId = yield* select(getMyStoreBundlesId);
  if (myStoreBundlesId) {
    yield put(sendDeleteBundlesSectionRequest({ myStoreBundlesId, sectionId }));
  }
}

export function* removeBundleRequestSaga({
  payload,
}: ReturnType<typeof actions.removeBundleRequestSaga>): SagaIterator {
  const myStoreBundlesId = yield* select(getMyStoreBundlesId);
  const { sectionId, rowId } = payload;
  if (myStoreBundlesId) {
    yield put(sendRemoveBundleRequest({ myStoreBundlesId, sectionId, bundleId: rowId }));
  }
}

export function* updateBundleDesignationRequestSaga({
  payload,
}: ReturnType<typeof actions.updateBundleDesignationRequestSaga>): SagaIterator {
  const myStoreBundlesId = yield* select(getMyStoreBundlesId);
  if (myStoreBundlesId) {
    yield put(sendUpdateBundleDesignation({ myStoreBundlesId, ...payload }));
  }
}

export function* updateBundleCodeRequestSaga({
  payload,
}: ReturnType<typeof actions.updateBundleCodeRequestSaga>): SagaIterator {
  const myStoreBundlesId = yield* select(getMyStoreBundlesId);
  if (myStoreBundlesId) {
    yield put(sendUpdateBundleCode({ myStoreBundlesId, ...payload }));
  }
}

export function* updateBundlePriceRequestSaga({
  payload,
}: ReturnType<typeof actions.updateBundlePriceRequestSaga>): SagaIterator {
  const myStoreBundlesId = yield* select(getMyStoreBundlesId);
  if (myStoreBundlesId) {
    yield put(sendUpdateBundlePrice({ myStoreBundlesId, ...payload }));
  }
}

export function* subscribeTiresRequestSaga(): SagaIterator {
  yield put(setMyStoreTiresSearchStatus(LOADING));
  yield put(sendSubscribeTiresRequest());
}

export function* getTiresResponseSaga(action: WsResponse<GetMyStoreTiresResponse>): SagaIterator {
  function* reducersUpdateMyStoreTires() {
    const { myStoreTiresId, brands } = action.payload;
    yield put(setMyStoreTiresId({ myStoreTiresId }));
    yield put(setMyStoreTireBrands({ brands }));
    yield put(setMyStoreTiresSearchStatus(FOUND));
  }

  switch (action.queryResponseType) {
    case QueryResponseType.COMMAND_RESPONSE_ERROR:
    case QueryResponseType.COMMAND_RESPONSE_INVALID: {
      notifyTop(
        'error',
        AppTranslation.t('my_store.tires.command.response.invalid.title', 'Tires discounts not saved!'),
        AppTranslation.t('my_store.tires.command.response.invalid.message', 'Something went wrong'),
      );
      break;
    }
    case QueryResponseType.COMMAND_RESPONSE_NO_CHANGE: {
      yield put(clearMyStoreTiresChanges());
      break;
    }
    case QueryResponseType.COMMAND_RESPONSE_SUCCESS: {
      notifyTop(
        'success',
        AppTranslation.t('my_store.tires.command.response.success.title', 'Tires discounts saved'),
        AppTranslation.t('my_store.tires.command.response.success.message', 'Changes has been saved.'),
      );
      break;
    }
    case QueryResponseType.EVENT_UPDATE:
    case QueryResponseType.INIT:
    case QueryResponseType.COMMAND_LOOKUP_RESPONSE: {
      yield* reducersUpdateMyStoreTires();
      break;
    }
  }
}

export function* updateMyStoreTireDiscountListRequest({
  payload,
}: ReturnType<typeof actions.updateMyStoreTireDiscountListRequest>): SagaIterator {
  const myStoreTiresId = yield* select(getMyStoreTiresId);
  if (myStoreTiresId) {
    const updateMyStoreTireDiscountList: UpdateMyStoreTireDiscount[] = [];
    for (const value of payload.values()) {
      updateMyStoreTireDiscountList.push({
        myStoreTiresId,
        brandId: value.brandId,
        tireRimSizeType: value.tireRimSizeType,
        discount: value.discount,
      });
    }

    yield put(sendUpdateMyStoreTireDiscountListRequest({ myStoreTiresId, updateMyStoreTireDiscountList }));
  }
}

export function* renameReferencesSectionRequestSaga({
  payload,
}: ReturnType<typeof actions.renameReferencesSectionRequestSaga>): SagaIterator {
  const { sectionId, title } = payload;
  const myStoreReferencesId = yield* select(getMyStoreReferencesId);
  if (myStoreReferencesId) {
    yield put(sendRenameReferencesSectionRequest({ myStoreReferencesId, sectionId, title }));
  }
}

export function* addReferencesSectionRequestSaga(): SagaIterator {
  const myStoreReferencesId = yield* select(getMyStoreReferencesId);
  const sectionId = v4();
  if (myStoreReferencesId) {
    yield put(sendAddReferencesSectionRequest({ myStoreReferencesId, sectionId, title: '' }));
  }
}

export function* moveReferencesSectionRequestSaga({
  payload,
}: ReturnType<typeof actions.moveReferencesSectionRequestSaga>): SagaIterator {
  const { active, over } = payload;
  if (over && active.id === over.id) return;
  const myStoreReferencesId = yield* select(getMyStoreReferencesId);
  const references = yield* select(getReferencesSections);
  const index =
    references.sections.data?.findIndex((referenceSection) => referenceSection.sectionId === over?.id) ?? -1;
  if (index > -1 && myStoreReferencesId) {
    yield put(reorderReferences(payload));
    yield put(sendMoveReferenceSectionRequest({ myStoreReferencesId, sectionId: active.id.toString(), index }));
  }
}

export function* getReferencesResponseSaga(action: WsResponse<GetMyStoreReferencesResponse>): SagaIterator {
  yield put(setReferencesSection(action.payload));
  yield put(setReferencesStatus(FOUND));
}

export function* getReferencesRequestSaga(): SagaIterator {
  yield put(setReferencesStatus(LOADING));
  yield put(sendGetReferencesRequest());
}

export function* addReferenceRequestSaga({
  payload,
}: ReturnType<typeof actions.addReferenceRequestSaga>): SagaIterator {
  const { sectionId } = payload;
  const myStoreReferencesId = yield* select(getMyStoreReferencesId);
  const referenceId = v4();
  if (myStoreReferencesId) {
    yield put(addNewReference({ myStoreReferencesId, referenceId, sectionId }));
  }
}

export function* removeReferenceSectionRequestSaga({
  payload,
}: ReturnType<typeof actions.removeReferenceSectionRequestSaga>): SagaIterator {
  const { sectionId } = payload;
  const myStoreReferencesId = yield* select(getMyStoreReferencesId);
  if (myStoreReferencesId) {
    yield put(sendDeleteReferencesSectionRequest({ myStoreReferencesId, sectionId }));
  }
}

export function* removeReferenceRequestSaga({
  payload,
}: ReturnType<typeof actions.removeReferenceRequestSaga>): SagaIterator {
  const { sectionId, rowId } = payload;
  const myStoreReferencesId = yield* select(getMyStoreReferencesId);
  const reference = yield* select((state: RootState) => getSectionReference(state, { sectionId, referenceId: rowId }));
  if (myStoreReferencesId && reference) {
    yield put(removeReference({ myStoreReferencesId, sectionId, referenceId: rowId }));
    if (!reference.isDraft) {
      yield put(sendRemoveReferenceRequest({ myStoreReferencesId, sectionId, referenceId: rowId }));
    }
  }
}

export function* createOrUpdateReferenceSaga({
  payload,
}: ReturnType<typeof actions.createOrUpdateReferenceSaga>): SagaIterator {
  const { sectionId, referenceId, fieldUpdate } = payload;
  const myStoreReferencesId = yield* select(getMyStoreReferencesId);
  const reference = yield* select((state: RootState) =>
    getSectionReference(state, { sectionId: payload.sectionId, referenceId: payload.referenceId }),
  );
  if (myStoreReferencesId && reference) {
    const { isDraft, ...data } = reference;
    const refToSend = { ...data, ...fieldUpdate };
    yield put(
      updateReferenceInSection({
        sectionId,
        referenceId,
        fieldUpdate,
      }),
    );

    if (
      refToSend.referenceNumber &&
      refToSend.designation &&
      refToSend.clientPriceVatExcluded &&
      refToSend.garagePriceVatExcluded
    ) {
      const referencePayload = {
        myStoreReferencesId,
        sectionId,
        referenceId: refToSend.referenceId,
        referenceNumber: refToSend.referenceNumber,
        designation: refToSend.designation,
        clientPriceVatExcluded: Number(refToSend.clientPriceVatExcluded),
        garagePriceVatExcluded: Number(refToSend.garagePriceVatExcluded),
      };
      yield put(
        isDraft
          ? actions.createReferenceRequestSaga(referencePayload)
          : actions.updateReferenceRequestSaga(referencePayload),
      );
    }
  }
}

export function* updateReferenceRequestSaga({
  payload,
}: ReturnType<typeof actions.updateReferenceRequestSaga>): SagaIterator {
  yield put(sendUpdateReferenceRequest(payload));
}

export function* createReferenceRequestSaga({
  payload,
}: ReturnType<typeof actions.createReferenceRequestSaga>): SagaIterator {
  yield put(sendCreateReferenceRequest(payload));
}

export function* MyStoreSagas(): SagaIterator {
  yield takeEvery(actions.initMyStoreRequest.type, sagaGuard(initMyStoreSaga));
  yield takeEvery(actions.getBundlesRequestSaga.type, sagaGuard(getBundlesRequestSaga));
  yield takeEvery(actions.getBundlesResponseSaga.type, sagaGuard(getBundlesResponseSaga));
  yield takeEvery(actions.moveBundlesSectionRequestSaga.type, sagaGuard(moveBundlesSectionRequestSaga));
  yield takeEvery(actions.addBundlesSectionRequestSaga.type, sagaGuard(addBundlesSectionRequestSaga));
  yield takeEvery(actions.removeBundleSectionReuestSaga.type, sagaGuard(removeBundleSectionReuestSaga));
  yield takeEvery(actions.renameBundlesSectionRequestSaga.type, sagaGuard(renameBundlesSectionRequestSaga));
  yield takeEvery(actions.addBundleRequestSaga.type, sagaGuard(addBundleRequestSaga));
  yield takeEvery(actions.removeBundleRequestSaga.type, sagaGuard(removeBundleRequestSaga));
  yield takeEvery(actions.updateBundleDesignationRequestSaga.type, sagaGuard(updateBundleDesignationRequestSaga));
  yield takeEvery(actions.updateBundleCodeRequestSaga.type, sagaGuard(updateBundleCodeRequestSaga));
  yield takeEvery(actions.updateBundlePriceRequestSaga.type, sagaGuard(updateBundlePriceRequestSaga));
  yield takeEvery(actions.subscribeTiresRequestSaga.type, sagaGuard(subscribeTiresRequestSaga));
  yield takeEvery(actions.getTiresResponseSaga.type, sagaGuard(getTiresResponseSaga));
  yield takeEvery(actions.updateMyStoreTireDiscountListRequest.type, sagaGuard(updateMyStoreTireDiscountListRequest));

  //MyStoreReferences
  yield takeEvery(actions.getReferencesRequestSaga.type, sagaGuard(getReferencesRequestSaga));
  yield takeEvery(actions.getReferencesResponseSaga.type, sagaGuard(getReferencesResponseSaga));
  yield takeEvery(actions.moveReferencesSectionRequestSaga.type, sagaGuard(moveReferencesSectionRequestSaga));
  yield takeEvery(actions.addReferencesSectionRequestSaga.type, sagaGuard(addReferencesSectionRequestSaga));
  yield takeEvery(actions.removeReferenceSectionRequestSaga.type, sagaGuard(removeReferenceSectionRequestSaga));
  yield takeEvery(actions.renameReferencesSectionRequestSaga.type, sagaGuard(renameReferencesSectionRequestSaga));
  yield takeEvery(actions.addReferenceRequestSaga.type, sagaGuard(addReferenceRequestSaga));
  yield takeEvery(actions.removeReferenceRequestSaga.type, sagaGuard(removeReferenceRequestSaga));
  yield takeEvery(actions.createOrUpdateReferenceSaga.type, sagaGuard(createOrUpdateReferenceSaga));
  yield takeEvery(actions.updateReferenceRequestSaga.type, sagaGuard(updateReferenceRequestSaga));
  yield takeEvery(actions.createReferenceRequestSaga.type, sagaGuard(createReferenceRequestSaga));
}
